import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("form"); // form, success, error
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "#050505" }}>
        <div className="w-full max-w-sm text-center border border-[#1A1A1A] bg-[#0D0D0D] p-12">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="text-2xl font-bold uppercase tracking-tight text-[#F2F2F2] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
            Invalid Link
          </h1>
          <p className="text-[#A1A1A1] text-sm mb-6">No reset token found. Please request a new password reset.</p>
          <Link to="/forgot-password">
            <Button className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6">
              Request New Link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setStatus("success");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (status === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "#050505" }}>
        <div className="w-full max-w-sm text-center border border-[#1A1A1A] bg-[#0D0D0D] p-12">
          <CheckCircle className="h-12 w-12 text-[#39FF14] mx-auto mb-6" strokeWidth={1.5} />
          <h1 data-testid="reset-success" className="text-2xl font-bold uppercase tracking-tight text-[#F2F2F2] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
            Password Reset
          </h1>
          <p className="text-[#A1A1A1] text-sm mb-6">Your password has been updated. You can now sign in.</p>
          <Link to="/login">
            <Button className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6">
              Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "#050505" }}>
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-3">Account</p>
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
            New Password
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
          <div className="space-y-2">
            <Label htmlFor="password" className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">New Password</Label>
            <Input
              id="password"
              data-testid="reset-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:ring-1 focus:ring-[#39FF14] placeholder:text-[#A1A1A1]/50"
              placeholder="Min 6 characters"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm" className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Confirm Password</Label>
            <Input
              id="confirm"
              data-testid="reset-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:ring-1 focus:ring-[#39FF14] placeholder:text-[#A1A1A1]/50"
              placeholder="Repeat password"
            />
          </div>

          {error && (
            <div className="p-3 border border-red-500/30 bg-red-500/10 text-red-400 text-sm" data-testid="reset-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="reset-submit"
            disabled={loading}
            className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6 disabled:opacity-50"
          >
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
